import { fetchRatesForBedbankOffersById, getBedbankRateKey } from 'actions/BedbankOfferActions'
import { useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from './reduxHooks'

interface BedbankRatesMap {
  [offerId: string]: {
    hotelOnlyRates: Array<App.BedbankRate>
    hotelBundleRates: Array<App.BedbankRate>
  }
}

interface FetchingStatusMap {
  [offerId: string]: boolean
}

function useBedbanksRates(
  occupancyByOfferId: Record<string, Array<App.Occupants>>,
  checkIn?: string,
  checkOut?: string,
  hookEnabled: boolean = true,
): [BedbankRatesMap, FetchingStatusMap] {
  const dispatch = useAppDispatch()

  const offerIds = useMemo(() => Object.keys(occupancyByOfferId), [occupancyByOfferId])

  // Get rates and fetching status for all offer IDs
  const bedbankOfferRates = useAppSelector(state => state.offer.bedbankOfferRates)
  const bedbankOfferRatesLoading = useAppSelector(state => state.offer.bedbankOfferRatesLoading)

  useEffect(() => {
    if (!hookEnabled || !checkIn || !checkOut || offerIds.length === 0) return

    // Dispatch a single-room fetch per unique (offerId, occupancy) combination
    // This ensures rate IDs match what was allocated per-room
    // Duplicate API calls are prevented by fetchRatesForBedbankOffersById's own missingOfferIds check
    offerIds.forEach(offerId => {
      const occupancies = occupancyByOfferId[offerId]
      if (!occupancies) return

      occupancies.forEach(occupancy => {
        dispatch(fetchRatesForBedbankOffersById([offerId], [occupancy], checkIn, checkOut))
      })
    })
  }, [dispatch, occupancyByOfferId, offerIds, checkIn, checkOut, hookEnabled])

  const ratesMap = useMemo(() => {
    const result: BedbankRatesMap = {}

    offerIds.forEach(offerId => {
      const occupancies = occupancyByOfferId[offerId]
      if (!checkIn || !checkOut || !occupancies) {
        result[offerId] = { hotelOnlyRates: [], hotelBundleRates: [] }
        return
      }

      // Merge rates from all individual occupancy fetches for this property
      const allHotelOnlyRates: Array<App.BedbankRate> = []
      const allHotelBundleRates: Array<App.BedbankRate> = []
      const seenRateIds = new Set<string>()

      occupancies.forEach(occupancy => {
        const filterKey = getBedbankRateKey([occupancy], checkIn, checkOut)
        const packages = bedbankOfferRates[offerId]?.[filterKey] ?? []

        packages.forEach(rate => {
          if (seenRateIds.has(rate.id)) return
          seenRateIds.add(rate.id)
          if (rate.isFlightBundle) {
            allHotelBundleRates.push(rate)
          } else {
            allHotelOnlyRates.push(rate)
          }
        })
      })

      result[offerId] = {
        hotelOnlyRates: allHotelOnlyRates,
        hotelBundleRates: allHotelBundleRates,
      }
    })

    return result
  }, [offerIds, occupancyByOfferId, bedbankOfferRates, checkIn, checkOut])

  const fetchingMap = useMemo(() => {
    const result: FetchingStatusMap = {}

    offerIds.forEach(offerId => {
      const occupancies = occupancyByOfferId[offerId]
      if (!checkIn || !checkOut || !occupancies) {
        result[offerId] = false
        return
      }

      result[offerId] = occupancies.some(occupancy => {
        const filterKey = getBedbankRateKey([occupancy], checkIn, checkOut)
        const isActivelyLoading = bedbankOfferRatesLoading[offerId]?.[filterKey] ?? false
        const hasNoData = bedbankOfferRates[offerId]?.[filterKey] === undefined
        return isActivelyLoading || hasNoData
      })
    })

    return result
  }, [offerIds, occupancyByOfferId, bedbankOfferRatesLoading, bedbankOfferRates, checkIn, checkOut])

  return [ratesMap, fetchingMap]
}

export default useBedbanksRates
